'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useHydration } from '@/hooks/use-hydration';
import { E, editorialBtnInlineBase, editorialErrorActionsRow } from '@/lib/editorial';

interface DeploymentGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const PUBLIC_PAGES = [
  '/',
  '/auth/login',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/action',
  '/auth/complete-profile',
  '/membership',
  '/about',
  '/contact',
  '/services',
  '/terms',
  '/privacy',
  '/unsubscribe',
  // Publicly shared initiatives: opened by visitors who are not signed in.
  '/experience',
];

const INITIAL_DELAY_MS = 1000;
const INTERVAL_AFTER_FAILURE_MS = 5000;
const INTERVAL_AFTER_SUCCESS_MS = 15000;
const MONITOR_WINDOW_MS = 60_000;
const HARD_FAILURE_THRESHOLD = 2;
const DEPLOY_UI_JITTER_MIN_MS = 200;
const DEPLOY_UI_JITTER_MAX_MS = 900;
const SCHEDULE_JITTER_MAX_MS = 800;

export function DeploymentGuard({ children, fallback }: DeploymentGuardProps) {
  const [isDeploying, setIsDeploying] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const isHydrated = useHydration();
  const pathname = usePathname();

  const isEnabled = process.env.NEXT_PUBLIC_ENABLE_DEPLOYMENT_GUARD !== 'false';

  const isPublicPage = React.useMemo(() => {
    if (!pathname) return false;
    return PUBLIC_PAGES.some((page) => pathname === page || pathname.startsWith(`${page}/`));
  }, [pathname]);

  React.useEffect(() => {
    if (!isHydrated || !isEnabled || isPublicPage) return;

    let cancelled = false;
    /** Mutable so the 60s monitor reads the latest streak (avoids a stale closure). */
    const health = { hardStreak: 0 };
    let nextDelayBase = INTERVAL_AFTER_FAILURE_MS;
    let chainTimer: ReturnType<typeof setTimeout> | null = null;
    let deployDebouncer: ReturnType<typeof setTimeout> | null = null;
    let monitorEndTimer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;

    const clearDeployDebouncer = () => {
      if (deployDebouncer) {
        clearTimeout(deployDebouncer);
        deployDebouncer = null;
      }
    };

    const scheduleDeployUI = () => {
      clearDeployDebouncer();
      const jitter =
        DEPLOY_UI_JITTER_MIN_MS +
        Math.random() * (DEPLOY_UI_JITTER_MAX_MS - DEPLOY_UI_JITTER_MIN_MS);
      deployDebouncer = setTimeout(() => {
        deployDebouncer = null;
        if (!cancelled && document.visibilityState === 'visible') setIsDeploying(true);
      }, jitter);
    };

    const scheduleChain = () => {
      if (chainTimer) {
        clearTimeout(chainTimer);
        chainTimer = null;
      }
      if (cancelled || document.visibilityState === 'hidden') return;
      const jitter = Math.floor(Math.random() * SCHEDULE_JITTER_MAX_MS);
      chainTimer = setTimeout(() => {
        chainTimer = null;
        void runCheck();
      }, nextDelayBase + jitter);
    };

    async function runCheck(): Promise<void> {
      if (cancelled || document.visibilityState === 'hidden') return;
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await fetch('/api/health', {
          method: 'HEAD',
          cache: 'no-store',
          signal: AbortSignal.timeout(10000),
        });

        if (response.ok) {
          health.hardStreak = 0;
          nextDelayBase = INTERVAL_AFTER_SUCCESS_MS;
          clearDeployDebouncer();
          setIsDeploying(false);
          return;
        }

        if (response.status === 408) {
          nextDelayBase = Math.min(22_000, nextDelayBase + 2500);
          clearDeployDebouncer();
          return;
        }

        if (response.status >= 500) {
          nextDelayBase = INTERVAL_AFTER_FAILURE_MS;
          health.hardStreak += 1;
          if (health.hardStreak >= HARD_FAILURE_THRESHOLD) {
            scheduleDeployUI();
          }
          return;
        }

        health.hardStreak = 0;
        nextDelayBase = INTERVAL_AFTER_FAILURE_MS;
        clearDeployDebouncer();
        setIsDeploying(false);
      } catch {
        // Timeouts, AbortError, network / ad-block — do not advance server-error streak
        nextDelayBase = Math.min(22_000, nextDelayBase + 2500);
        clearDeployDebouncer();
      } finally {
        inFlight = false;
        if (!cancelled && document.visibilityState === 'visible') {
          scheduleChain();
        }
      }
    }

    const kick = () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      void runCheck();
    };

    const initialTimer = setTimeout(kick, INITIAL_DELAY_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (chainTimer) {
          clearTimeout(chainTimer);
          chainTimer = null;
        }
        return;
      }
      if (chainTimer) {
        clearTimeout(chainTimer);
        chainTimer = null;
      }
      void runCheck();
    };

    document.addEventListener('visibilitychange', onVisibility);

    monitorEndTimer = setTimeout(() => {
      if (chainTimer) {
        clearTimeout(chainTimer);
        chainTimer = null;
      }
      clearDeployDebouncer();
      if (health.hardStreak >= HARD_FAILURE_THRESHOLD && !cancelled) {
        setIsDeploying(false);
      }
    }, MONITOR_WINDOW_MS);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (chainTimer) clearTimeout(chainTimer);
      if (monitorEndTimer) clearTimeout(monitorEndTimer);
      clearDeployDebouncer();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isHydrated, isEnabled, isPublicPage, retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    setIsDeploying(false);
  };

  if (!isHydrated || !isEnabled || isPublicPage) {
    return <>{children}</>;
  }

  if (isDeploying) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div style={{ ...E.page, padding: 0 }}>
        <div className="e-pad" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 56, paddingBottom: 80 }}>
          <div style={E.label}>Service · Availability</div>

          <p
            style={{
              fontFamily: E.serif,
              fontSize: 'clamp(56px, 12vw, 96px)',
              fontWeight: 400,
              fontStyle: 'italic',
              lineHeight: 0.95,
              margin: '0 0 8px',
              color: E.goldDeep,
              opacity: 0.4,
              letterSpacing: '-0.02em',
            }}
          >
            …
          </p>

          <h1 role="alert" style={{ ...E.h1, fontSize: 'clamp(32px, 4.5vw, 48px)', marginBottom: 16 }}>
            Service temporarily unavailable.
          </h1>

          <p style={{ ...E.body, color: E.muted, maxWidth: 520, marginBottom: retryCount > 2 ? 20 : 28 }}>
            We&apos;re currently updating our services. This usually takes just a few moments.
          </p>

          {retryCount > 2 && (
            <p
              style={{
                ...E.body,
                fontSize: 14,
                color: E.muted,
                maxWidth: 520,
                marginBottom: 28,
                padding: 16,
                background: E.cream2,
                border: `1px solid ${E.rule}`,
              }}
            >
              If this continues, please wait a few minutes and try again. We&apos;re restoring full service as
              quickly as possible.
            </p>
          )}

          <div style={editorialErrorActionsRow}>
            <button type="button" onClick={handleRetry} style={{ ...E.btn, ...editorialBtnInlineBase }}>
              Check again{retryCount > 0 ? ` (${retryCount})` : ''}
            </button>
            <Link href="/" style={{ ...E.btnGhost, ...editorialBtnInlineBase }}>
              Return home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
